import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, BehaviorSubject } from 'rxjs';
import { getConfig } from '../config.service';
import { AuthService } from '../auth/auth.service';
import { SiteService } from '../site-selector/site.service';

export interface FavoritePoi {
	personalNumber: string | null;
	poiId: number;
	siteId: number;
}

export interface FavoritePoiItem {
	sourceKind: string;
	title: string;
	description: string;
	defectSeverityLevelId: number;
	sourceId: number;
	poiTypeId: number;
}

export interface FavoritePoiList {
	favoritePoiItems: FavoritePoiItem[];
}

export interface BooleanResult {
	result: boolean;
}

export enum FavoritePoiAction {
	Add,
	Remove
}

export interface FavoritePoiActionResult {
	action: FavoritePoiAction;
	success: boolean;
}

interface CachedFavorites {
	siteId: number;
	items: FavoritePoiItem[];
	timestamp: number;
}

const FAVORITES_STORAGE_KEY_PREFIX = 'skomap_favorites_';

@Injectable({ providedIn: 'root' })
export class FavoritePoiService {
	private http = inject(HttpClient);
	private authService = inject(AuthService);
	private siteService = inject(SiteService);

	private userFavoritePois: FavoritePoiItem[] = [];
	private loaded = false;
	private loading$ = new BehaviorSubject<boolean>(false);
	private refreshing$ = new BehaviorSubject<boolean>(false);
	private favoritesSubject = new BehaviorSubject<FavoritePoiItem[]>([]);

	readonly isLoading$ = this.loading$.asObservable();
	readonly isRefreshing$ = this.refreshing$.asObservable();
	readonly favorites$ = this.favoritesSubject.asObservable();

	async isFavoritePoi(poiId: number): Promise<boolean> {
		await this.loadUserFavoritePois();
		return this.userFavoritePois.some(p => p.sourceId === poiId);
	}

	async addPoiToFavorites(poiId: number): Promise<FavoritePoiActionResult> {
		const siteId = this.siteService.selectedSite?.id;
		if (!siteId) {
			return { action: FavoritePoiAction.Add, success: false };
		}

		const payload: FavoritePoi = {
			personalNumber: null, // Backend will get from auth context
			poiId,
			siteId
		};

		try {
			const url = `${getConfig().api.middlewareUrl}/api/FavoritePoi/addandget`;
			const response = await firstValueFrom(
				this.http.post<FavoritePoiItem>(url, payload)
			);

			if (response) {
				this.userFavoritePois.push(response);

				// Sync storage and emit update
				this.persistToStorage(siteId, this.userFavoritePois);
				this.favoritesSubject.next([...this.userFavoritePois]);

				return { action: FavoritePoiAction.Add, success: true };
			}
			return { action: FavoritePoiAction.Add, success: false };
		} catch (error) {
			console.error('[FavoritePoiService] Failed to add favorite:', error);
			return { action: FavoritePoiAction.Add, success: false };
		}
	}

	async removePoiFromFavorites(poiId: number): Promise<FavoritePoiActionResult> {
		const siteId = this.siteService.selectedSite?.id;
		if (!siteId) {
			return { action: FavoritePoiAction.Remove, success: false };
		}

		const payload: FavoritePoi = {
			personalNumber: null,
			poiId,
			siteId
		};

		try {
			const url = `${getConfig().api.middlewareUrl}/api/FavoritePoi/remove`;
			const response = await firstValueFrom(
				this.http.post<BooleanResult>(url, payload)
			);

			if (response?.result) {
				const index = this.userFavoritePois.findIndex(p => p.sourceId === poiId);
				if (index !== -1) {
					this.userFavoritePois.splice(index, 1);
				}

				// Sync storage and emit update
				this.persistToStorage(siteId, this.userFavoritePois);
				this.favoritesSubject.next([...this.userFavoritePois]);

				return { action: FavoritePoiAction.Remove, success: true };
			}
			return { action: FavoritePoiAction.Remove, success: false };
		} catch (error) {
			console.error('[FavoritePoiService] Failed to remove favorite:', error);
			return { action: FavoritePoiAction.Remove, success: false };
		}
	}

	async toggleFavorite(poiId: number): Promise<FavoritePoiActionResult> {
		const isFavorite = await this.isFavoritePoi(poiId);
		if (isFavorite) {
			return this.removePoiFromFavorites(poiId);
		}
		return this.addPoiToFavorites(poiId);
	}

	async getUserFavoritePois(forceReload = false): Promise<FavoritePoiItem[]> {
		await this.loadUserFavoritePois(forceReload);
		return [...this.userFavoritePois];
	}

	private async loadUserFavoritePois(forceReload = false): Promise<void> {
		if (this.loaded && !forceReload) {
			return;
		}

		if (this.loading$.value) {
			// Already loading, wait for completion
			return new Promise(resolve => {
				const sub = this.loading$.subscribe(loading => {
					if (!loading) {
						sub.unsubscribe();
						resolve();
					}
				});
			});
		}

		this.loading$.next(true);

		try {
			const userData = await firstValueFrom(this.authService.userData$);
			const siteId = this.siteService.selectedSite?.id;

			if (!userData?.preferred_username || !siteId) {
				this.userFavoritePois = [];
				this.loaded = true;
				return;
			}

			const personalNumber = encodeURIComponent(userData.preferred_username);
			const timestamp = Date.now();
			const url = `${getConfig().api.middlewareUrl}/api/FavoritePoi/getlist/${personalNumber}/${siteId}?v=${timestamp}`;

			const response = await firstValueFrom(
				this.http.get<FavoritePoiList>(url)
			);

			this.userFavoritePois = response?.favoritePoiItems ?? [];
			this.loaded = true;

			// Persist to storage and emit to subscribers
			this.persistToStorage(siteId, this.userFavoritePois);
			this.favoritesSubject.next([...this.userFavoritePois]);

			console.log(`[FavoritePoiService] Loaded ${this.userFavoritePois.length} favorite POIs`);
		} catch (error) {
			console.error('[FavoritePoiService] Failed to load favorites:', error);
			this.userFavoritePois = [];
			this.loaded = true;
		} finally {
			this.loading$.next(false);
		}
	}

	// Preload favorites in background (non-blocking)
	preloadFavorites(): void {
		const siteId = this.siteService.selectedSite?.id;
		if (!siteId) return;

		// 1. Load from storage immediately (if available)
		const stored = this.loadFromStorage(siteId);
		if (stored) {
			this.userFavoritePois = stored;
			this.loaded = true;
			this.favoritesSubject.next([...stored]);
			console.log(`[FavoritePoiService] Loaded ${stored.length} favorites from storage`);
		}

		// 2. Then refresh from API in background
		if (!this.loading$.value) {
			this.loadUserFavoritePois().catch(err => {
				console.error('[FavoritePoiService] Failed to preload favorites:', err);
			});
		}
	}

	// Call when site changes to reload favorites
	clearCache(): void {
		const siteId = this.siteService.selectedSite?.id;
		if (siteId) {
			this.clearStorage(siteId);
		}
		this.loaded = false;
		this.userFavoritePois = [];
		this.favoritesSubject.next([]);
	}

	// Get cached favorites immediately (from memory or storage)
	getCachedFavorites(): FavoritePoiItem[] {
		// Return memory cache if available
		if (this.loaded && this.userFavoritePois.length > 0) {
			return [...this.userFavoritePois];
		}

		// Try sessionStorage
		const siteId = this.siteService.selectedSite?.id;
		if (siteId) {
			const stored = this.loadFromStorage(siteId);
			if (stored) {
				this.userFavoritePois = stored;
				this.favoritesSubject.next([...stored]);
				return [...stored];
			}
		}

		return [];
	}

	// Background refresh (non-blocking, emits to favorites$)
	async refreshFavoritesInBackground(): Promise<void> {
		if (this.refreshing$.value) return;

		this.refreshing$.next(true);

		try {
			const userData = await firstValueFrom(this.authService.userData$);
			const siteId = this.siteService.selectedSite?.id;

			if (!userData?.preferred_username || !siteId) {
				return;
			}

			const personalNumber = encodeURIComponent(userData.preferred_username);
			const timestamp = Date.now();
			const url = `${getConfig().api.middlewareUrl}/api/FavoritePoi/getlist/${personalNumber}/${siteId}?v=${timestamp}`;

			const response = await firstValueFrom(this.http.get<FavoritePoiList>(url));
			const items = response?.favoritePoiItems ?? [];

			// Update both caches
			this.userFavoritePois = items;
			this.loaded = true;
			this.persistToStorage(siteId, items);

			// Emit to subscribers
			this.favoritesSubject.next([...items]);

			console.log(`[FavoritePoiService] Background refresh: ${items.length} favorites`);
		} catch (error) {
			console.error('[FavoritePoiService] Background refresh failed:', error);
			// Don't clear cache on error - keep showing stale data
		} finally {
			this.refreshing$.next(false);
		}
	}

	// Storage methods
	private getStorageKey(siteId: number): string {
		return `${FAVORITES_STORAGE_KEY_PREFIX}${siteId}`;
	}

	private persistToStorage(siteId: number, items: FavoritePoiItem[]): void {
		try {
			const data: CachedFavorites = {
				siteId,
				items,
				timestamp: Date.now()
			};
			sessionStorage.setItem(this.getStorageKey(siteId), JSON.stringify(data));
		} catch (error) {
			console.error('[FavoritePoiService] Failed to persist to storage:', error);
		}
	}

	private loadFromStorage(siteId: number): FavoritePoiItem[] | null {
		try {
			const stored = sessionStorage.getItem(this.getStorageKey(siteId));
			if (!stored) return null;

			const data: CachedFavorites = JSON.parse(stored);
			return data.items;
		} catch {
			return null;
		}
	}

	private clearStorage(siteId: number): void {
		sessionStorage.removeItem(this.getStorageKey(siteId));
	}
}
